import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  InputNumber,
  List,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from "antd";
import { useTranslation } from "react-i18next";
import { errorText, openBlob } from "../api";
import { formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import {
  createLohnsteuerbescheinigungen,
  createUvJahresmeldung,
  filingPayloadBlob,
  listFilings
} from "./api";

export const FILING_LABELS = {
  lohnsteuer_anmeldung: "Lohnsteuer-Anmeldung",
  beitragsnachweis: "Beitragsnachweis",
  deuev: "DEÜV-Meldung",
  soka_bau: "SOKA-BAU Meldung",
  lohnsteuerbescheinigung: "Lohnsteuerbescheinigung",
  uv_jahresmeldung: "UV-Jahresmeldung (BG)"
};
const STATUS_COLOR = {
  draft: "default",
  ready: "processing",
  exported: "blue",
  transmitted: "green",
  accepted: "success",
  rejected: "error",
  corrected: "warning"
};
const MELDEGRUND = {
  10: "Anmeldung",
  20: "Sofortmeldung",
  30: "Abmeldung",
  50: "Jahresmeldung",
  51: "Unterbrechung"
};

const subject = (f, t) => {
  if (f.type === "deuev")
    return `${f.data?.employee?.name || ""} · ${MELDEGRUND[f.data?.meldegrund] ? t(MELDEGRUND[f.data.meldegrund]) : f.data?.meldegrund}`;
  if (f.type === "beitragsnachweis") return f.data?.krankenkasse || "";
  if (f.type === "lohnsteuerbescheinigung")
    return `${f.data?.employee?.firstName || ""} ${f.data?.employee?.lastName || ""}`.trim();
  return "";
};

export const FilingRow = ({ filing }) => {
  const { t } = useTranslation("accounting");
  const download = () =>
    openBlob(
      filingPayloadBlob(filing._id),
      `${filing.type}_${filing.period}.${filing.payloadFormat === "csv" ? "csv" : "txt"}`
    ).catch(err => message.error(errorText(err)));
  return (
    <List.Item
      actions={[
        <Button key="dl" onClick={download}>
          {t("Download")}
        </Button>
      ]}
    >
      <List.Item.Meta
        title={
          <Space wrap size={[8, 4]}>
            <span className="acct-wrap">
              {FILING_LABELS[filing.type] ? t(FILING_LABELS[filing.type]) : filing.type}{" "}
              {filing.period}
            </span>
            <Tag color={STATUS_COLOR[filing.status]}>{t(`filing_${filing.status}`)}</Tag>
            {filing.transport === "elster" ? <Tag>ELSTER</Tag> : <Tag>{t("manual upload")}</Tag>}
          </Space>
        }
        description={
          <span className="acct-wrap">
            {filing.amountDue ? (
              <>
                <Amount cents={filing.amountDue} large />
                <br />
              </>
            ) : null}
            {[subject(filing, t), filing.dueDate ? `${t("due")} ${formatDate(filing.dueDate)}` : ""]
              .filter(Boolean)
              .join(" · ")}
            {filing.data?.blockers?.length ? (
              <Typography.Text type="danger" style={{ display: "block" }}>
                {filing.data.blockers.join(" ")}
              </Typography.Text>
            ) : null}
          </span>
        }
      />
    </List.Item>
  );
};

const PayrollFilings = () => {
  const { t } = useTranslation("accounting");
  const [year, setYear] = useState(new Date().getFullYear());
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listFilings({ year });
      setFilings(res.data.filings || []);
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const yearly = async (fn, success) => {
    setLoading(true);
    try {
      await fn(year);
      message.success(success);
      await load();
    } catch (err) {
      message.error(errorText(err));
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Card size="small" style={{ marginBottom: 16 }} title={t("Year-end")}>
        <Space wrap>
          <InputNumber
            value={year}
            onChange={v => v && setYear(v)}
            min={2020}
            max={2100}
            aria-label={t("Year")}
          />
          <Popconfirm
            title={t("Create the Lohnsteuerbescheinigung for every employee of the year?")}
            onConfirm={() =>
              yearly(createLohnsteuerbescheinigungen, t("Lohnsteuerbescheinigungen created"))
            }
          >
            <Button>{t("Create Lohnsteuerbescheinigungen")}</Button>
          </Popconfirm>
          <Popconfirm
            title={t("Create the UV-Jahresmeldung (Entgelt per Gefahrtarifstelle)?")}
            onConfirm={() => yearly(createUvJahresmeldung, t("UV-Jahresmeldung created"))}
          >
            <Button>{t("Create UV-Jahresmeldung")}</Button>
          </Popconfirm>
        </Space>
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message={t(
            "Both are idempotent: running them again refreshes the existing filings instead of creating duplicates."
          )}
        />
      </Card>
      <Card size="small" title={`${t("Payroll filings")} ${year}`}>
        <List
          dataSource={filings}
          renderItem={f => <FilingRow filing={f} />}
          locale={{ emptyText: t("No filings for this year yet.") }}
        />
      </Card>
    </Spin>
  );
};

export default PayrollFilings;
